import React, { useEffect, useState } from "react";
import { ShieldCheck, RefreshCw } from "lucide-react";
import { INTEGRATIONS, probeIntegration, IntegrationResult, IntegrationState, STATE_META } from "@/api/diagnostics";
import { useI18n } from "@/i18n";

export function StateChip({ state, size = "sm" }: { state: IntegrationState; size?: "sm" | "md" }) {
  const { lang } = useI18n();
  const m = STATE_META[state];
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span data-testid={`state-chip-${state.toLowerCase()}`}
      className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${m.tone} ${pad}`}>
      {m[lang]}
    </span>
  );
}

export function IntegrationStatusPanel() {
  const { lang } = useI18n();
  const [results, setResults] = useState<Record<string, IntegrationResult>>({});
  const [loading, setLoading] = useState(true);

  async function run() {
    setLoading(true);
    setResults({});
    await Promise.all(
      INTEGRATIONS.map(async (def) => {
        const r = await probeIntegration(def);
        setResults((prev) => ({ ...prev, [def.key]: r }));
      }),
    );
    setLoading(false);
  }
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="panel p-5" data-testid="integration-status">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-foreground"><ShieldCheck className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Entegrasyon Durumu" : "Integration Status"}</h3>
        <button onClick={run} data-testid="integration-refresh" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:text-foreground">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> {lang === "tr" ? "Yenile" : "Refresh"}
        </button>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">{lang === "tr" ? "Her üretim veri yolu canlı olarak doğrulanır. Gerçek SDIP üretim backend'i birincil kaynaktır; eksik bağımlılıklar açıkça gösterilir, demo içerikle değiştirilmez." : "Every production data path is validated live. The real SDIP production backend is the primary source; missing dependencies are shown explicitly and never replaced with demo content."}</p>
      <div className="space-y-2">
        {INTEGRATIONS.map((def) => {
          const r = results[def.key];
          const state: IntegrationState = r ? r.state : "CHECKING";
          const status = r?.status ?? null;
          const missing = r?.missing;
          return (
            <div key={def.key} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid={`integration-row-${def.key}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground/90">{def.label[lang]}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  <span className="num">/api/atlas{def.path}</span> · {def.auth ? "Bearer" : (lang === "tr" ? "genel" : "public")} · {def.method}
                  {status != null ? ` · ${status}` : ""} · {def.uiRoute}
                  {missing ? ` · ${lang === "tr" ? "eksik" : "missing"}: ${missing}` : ""}
                </p>
              </div>
              <StateChip state={state} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
